import type { Rng } from '@campfire/engine';

/** Unbiased Fisher–Yates shuffle. Returns a new array; input untouched. */
export function shuffle<T>(items: readonly T[], rng: Rng): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [out[i], out[j]] = [out[j] as T, out[i] as T];
  }
  return out;
}

export interface DealOptions {
  /** Cards per hand, or 'all' to deal the whole deck as evenly as possible. */
  readonly perHand: number | 'all';
  /** First hand to receive a card (e.g. left of dealer). Default 0. */
  readonly startHand?: number;
}

export interface DealResult<T> {
  readonly hands: T[][];
  readonly remaining: T[];
}

/**
 * Deal round-robin from the top (index 0) of `deck`, like a human dealer.
 * Conserves every card: hands + remaining is a permutation of deck (FR-CARD-05).
 */
export function deal<T>(deck: readonly T[], handCount: number, { perHand, startHand = 0 }: DealOptions): DealResult<T> {
  if (!Number.isInteger(handCount) || handCount < 1) throw new RangeError('handCount must be a positive integer');
  if (!Number.isInteger(startHand) || startHand < 0 || startHand >= handCount) {
    throw new RangeError('startHand out of range');
  }
  const total = perHand === 'all' ? deck.length : perHand * handCount;
  if (perHand !== 'all' && (!Number.isInteger(perHand) || perHand < 0)) {
    throw new RangeError('perHand must be a non-negative integer or "all"');
  }
  if (total > deck.length) {
    throw new RangeError(`Cannot deal ${total} cards from a deck of ${deck.length}`);
  }
  const hands: T[][] = Array.from({ length: handCount }, () => []);
  for (let i = 0; i < total; i++) {
    (hands[(startHand + i) % handCount] as T[]).push(deck[i] as T);
  }
  return { hands, remaining: deck.slice(total) };
}
